import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, LogOut, Globe, BookMarked, Crown, Ticket, Feather } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { WorldFormDialog } from "@/components/WorldFormDialog";
import { Loader } from "@/components/Loader";
import { NotificationBell } from "@/components/NotificationBell";
import { AppearanceLink } from "@/components/AppearanceLink";
import { ROLE_LABEL } from "@/lib/perms";

const WorldCard = ({ w, i }) => (
  <Link to={`/worlds/${w.id}`} data-testid={`world-card-${w.id}`} style={{ animationDelay: `${i * 60}ms` }}
    className="rise group relative block rounded-2xl overflow-hidden border border-line bg-panel hover:border-amber-500/40 transition-colors">
    <div className="h-40 relative overflow-hidden">
      {w.cover_image ? (
        <img src={fileUrl(w.cover_image)} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
      ) : (
        <div className="w-full h-full bg-[radial-gradient(circle_at_30%_30%,rgba(245,158,11,0.25),transparent_60%)] bg-raised" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-panel to-transparent" />
      {w.is_public && <Globe className="absolute top-3 right-3 w-4 h-4 text-emerald-400" />}
    </div>
    <div className="p-5 -mt-6 relative">
      <span className="font-code text-[10px] uppercase tracking-[0.2em] text-amber-500/80">
        {w.kind === "fanfiction" ? `Fanfic · ${w.source_material || "Unknown fandom"}` : "Original"}{w.genre ? ` · ${w.genre}` : ""}
      </span>
      <h3 className="font-display text-2xl text-amber-50 mt-1 group-hover:text-amber-300 transition-colors">{w.name}</h3>
      <p className="text-sm text-slate-400 mt-2 line-clamp-2 min-h-[2.5rem]">{w.description || "An untold world."}</p>
      <p className="text-xs text-slate-500 mt-4 flex items-center gap-1.5"><BookMarked className="w-3.5 h-3.5" /> {w.page_count} pages</p>
    </div>
  </Link>
);

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [worlds, setWorlds] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/worlds").then((r) => setWorlds(r.data));
  const own = (worlds || []).filter((w) => w.my_role === "owner");
  const shared = (worlds || []).filter((w) => w.my_role !== "owner");
  useEffect(() => { load(); }, []);

  return (
    <div className="relative z-10 min-h-screen" data-testid="dashboard-page">
      <header className="flex items-center justify-between px-6 md:px-12 py-5 border-b border-line">
        <span className="font-display text-2xl text-amber-100">Lore<span className="text-amber-500">ly</span></span>
        <div className="flex items-center gap-4">
          {user.role === "admin" && (
            <Link to="/admin/promos" className="hidden sm:inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-amber-300" data-testid="admin-promos-link">
              <Ticket className="w-3.5 h-3.5" /> Admin
            </Link>
          )}
          <Link to="/writing" className="inline-flex items-center gap-1.5 text-xs rounded-full border border-line px-3 py-1.5 text-slate-300 hover:border-amber-500/40 hover:text-amber-300 transition-colors" data-testid="writing-link">
            <Feather className="w-3.5 h-3.5" /> Writing
          </Link>
          <AppearanceLink />
          <NotificationBell />
          <Link to="/billing" data-testid="plan-badge-link"
            className={`inline-flex items-center gap-1.5 text-xs font-code uppercase tracking-widest rounded-full border px-3 py-1 transition-colors ${user.is_pro ? "border-amber-500/50 text-amber-300 bg-amber-500/10" : "border-line text-slate-400 hover:text-amber-300 hover:border-amber-500/40"}`}>
            <Crown className="w-3.5 h-3.5" /> {user.is_pro ? "Pro" : "Upgrade"}
          </Link>
          {user.picture && <img src={user.picture} alt="" className="w-7 h-7 rounded-full border border-line" data-testid="user-avatar" />}
          <span className="text-sm text-slate-400 hidden sm:inline" data-testid="user-name">{user.name}</span>
          <button onClick={logout} className="p-2 rounded-full text-slate-400 hover:text-rose-400 hover:bg-veil/5 transition-colors" data-testid="logout-btn"><LogOut className="w-4 h-4" /></button>
        </div>
      </header>
      <main className="px-6 md:px-12 py-12 max-w-7xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div>
            <p className="font-code text-xs uppercase tracking-[0.2em] sm:tracking-[0.3em] text-amber-500/80 mb-3">Your library{!user.is_pro && worlds ? ` · ${own.length}/2 on Free` : ""}</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-amber-50">Worlds in the making</h1>
          </div>
          <button onClick={() => setOpen(true)} data-testid="create-world-btn"
            className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-6 py-3 text-onaccent font-semibold hover:bg-amber-400 transition-colors">
            <Plus className="w-4 h-4" /> New world
          </button>
        </div>
        {worlds === null ? <Loader /> : own.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-line p-16 text-center" data-testid="empty-worlds">
            <p className="font-display text-3xl text-amber-100">The map is blank.</p>
            <p className="text-slate-400 mt-2">Forge your first world to begin its wiki.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="worlds-grid">
            {own.map((w, i) => <WorldCard key={w.id} w={w} i={i} />)}
          </div>
        )}
        {shared.length > 0 && (
          <section className="mt-20" data-testid="shared-worlds">
            <p className="font-code text-xs uppercase tracking-[0.3em] text-amber-500/80 mb-3">Collaborations</p>
            <h2 className="font-display text-3xl sm:text-4xl text-amber-50 mb-8">Shared with me</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="shared-worlds-grid">
              {shared.map((w, i) => (
                <div key={w.id} className="relative">
                  <WorldCard w={w} i={i} />
                  <span className="absolute top-3 right-3 z-10 text-[10px] font-code uppercase tracking-widest rounded-full bg-black/70 border border-amber-500/40 text-[#fde68a] px-2 py-0.5" data-testid={`shared-role-${w.id}`}>{ROLE_LABEL[w.my_role]}</span>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
      <WorldFormDialog open={open} onOpenChange={setOpen} onSaved={load} />
    </div>
  );
}
